import { useEffect, useRef } from 'react';
import { ParamKeyValuePair, useSearchParams } from 'react-router-dom';
import {
  NavigationMenuItemDto,
  NavigationPageItemDto,
  NavItemLabelDto,
  PageDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  fetchNavigationCustomization,
  updateNavigationCustomization,
} from '../api/portalGateway/navigationCustomization';
import { NO_ROLE_ID } from '../api/portalGatewayClient';
import { useStore } from '../store';
import {
  mergeComparisonNavItemLabels,
  resolveComparisonDiscriminatorDisplayValue,
} from '../utils/comparisonDetailPageNav';
import { COMPARISON_ENTITIES_URL_PARAM } from '../utils/constants';
import { getDetailPageNavigationIdentity } from '../utils/detailPageNavigationIdentity';
import { navItemLabelsEqual } from '../utils/detailPageNavItemLabelLines';
import { prependRoleId } from '../utils/prependRoleId';
import { resolveDetailPageNavItemDisplay } from '../utils/resolveDetailPageNavItemDisplay';
import { equalPathname, pageWithRoleLink } from '../utils/urlPath';

import { useReactiveCachedPageFilterDefinitions } from './useReactiveCachedPageFilterDefinitions';

export type UseSyncFiltersToSearchParams = {
  filtersToSet: ParamKeyValuePair[] | null;
  allFilterUrlKeys: string[];
  navigationData?: NavigationMenuItemDto | NavigationPageItemDto;
  navItemId: string | null;
  pageData?: PageDto;
  pageFilterInstanceId?: string | null;
};

/**
 * Generic hook to synchronize filters with search parameters.
 * Not intended to be used directly, but rather as a helper for
 * useSyncAllFiltersToSearchParams.
 */
export function useSyncFiltersToSearchParams({
  filtersToSet,
  allFilterUrlKeys,
  navigationData,
  navItemId,
  pageData,
  pageFilterInstanceId,
}: UseSyncFiltersToSearchParams) {
  const roleId = useStore(useShallow((state) => state.activeUserRole?.id));

  const queryClient = useQueryClient();
  const updateMutation = useUpdateMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Keep refs for values the effect reads but should NOT re-trigger on.
  // The effect is driven by filtersToSet/allFilterUrlKeys and by the current
  // search string so menu navigation that clears ?query still re-syncs from
  // store. searchParams/setSearchParams objects are read from refs to avoid
  // unstable identity; only searchParams.toString() is a dependency.
  // setSearchParams must stay off the dep list (React Router v6 useCallback
  // depends on searchParams).
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const updateMutationRef = useRef(updateMutation);
  updateMutationRef.current = updateMutation;

  const roleIdRef = useRef(roleId);
  roleIdRef.current = roleId;

  const navigationDataRef = useRef(navigationData);
  navigationDataRef.current = navigationData;

  const navItemIdRef = useRef(navItemId);
  navItemIdRef.current = navItemId;

  const pageDataRef = useRef(pageData);
  pageDataRef.current = pageData;

  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  const cachedPageFilterDefinitions =
    useReactiveCachedPageFilterDefinitions(pageFilterInstanceId);

  const searchString = searchParams.toString();

  useEffect(() => {
    // only trigger syncback after filters are initialised and loaded
    if (filtersToSet != null) {
      const currentSearchParams = searchParamsRef.current;

      // build updated url params based on set filters
      const nextUrlSearchParams = new URLSearchParams(currentSearchParams);

      // Remove all filters from the url
      for (const fc of allFilterUrlKeys) {
        nextUrlSearchParams.delete(fc);
      }

      // Add new filters to the url
      for (const [key, value] of filtersToSet) {
        nextUrlSearchParams.append(key, value);
      }

      // update dynamic nav item
      const currentNavigationData = navigationDataRef.current;
      const currentNavItemId = navItemIdRef.current;
      const currentPageData = pageDataRef.current;
      const currentRoleId = roleIdRef.current;
      const currentMutation = updateMutationRef.current;
      const currentQueryClient = queryClientRef.current;

      // @deprecated 2026-04-08: Can be removed after reasonable time has passed and we expect users to have updated their current dynamic navigation items.
      if (
        currentNavigationData != null &&
        currentNavigationData.type === 'navigation-page-item' &&
        currentPageData?.type === 'detailPage'
      ) {
        const detailPageIdentity = getDetailPageNavigationIdentity(
          nextUrlSearchParams,
          currentPageData
        );

        if (detailPageIdentity != null) {
          // build new url
          let href = pageWithRoleLink(currentRoleId ?? NO_ROLE_ID, {
            pageType: 'detailPage',
            slug: currentNavigationData.completeSlug,
          });

          const persistedSearchString =
            detailPageIdentity.persistedSearchParams.toString();
          if (persistedSearchString.length > 0) {
            href = `${href}?${persistedSearchString}`;
          }
          // check if we already have a dynamic navigation item
          const previousNavigationCustomization =
            currentQueryClient.getQueryData(getQueryOptions().queryKey);
          const previousItem =
            previousNavigationCustomization?.data.dynamicNavigationItems.find(
              (nit) => nit.id === currentNavItemId
            );

          const isComparisonPage = nextUrlSearchParams.has(
            COMPARISON_ENTITIES_URL_PARAM
          );
          const filterDefinitions = isComparisonPage
            ? cachedPageFilterDefinitions
            : undefined;
          const comparisonDisplayValue = isComparisonPage
            ? resolveComparisonDiscriminatorDisplayValue(
                currentPageData,
                nextUrlSearchParams,
                filterDefinitions
              )
            : undefined;

          const useServerLabelLines =
            !isComparisonPage &&
            (currentPageData.detailPageNavItemConfig?.length ?? 0) > 0;

          const navItemLabels = useServerLabelLines
            ? (previousItem?.navItemLabels ?? [])
            : resolveDetailPageNavItemDisplay(
                currentPageData,
                nextUrlSearchParams,
                { detailsDiscriminatorDisplayValue: comparisonDisplayValue }
              );

          const pathnameChanged =
            previousItem != null && !equalPathname(previousItem.href, href);

          const effectiveNavItemLabels = useServerLabelLines
            ? navItemLabels
            : resolveEffectiveNavItemLabels({
                pathnameChanged,
                storedNavItemLabels: previousItem?.navItemLabels,
                resolvedFromUrl: navItemLabels,
                isComparisonAndFilterDataAvailable:
                  isComparisonPage && !!filterDefinitions,
              });

          const hrefChanged = !equalHref(previousItem?.href ?? '', href);
          const labelsChanged = useServerLabelLines
            ? false
            : !navItemLabelsEqual(
                previousItem?.navItemLabels ?? [],
                effectiveNavItemLabels
              );

          // proceed to update only if we have an item and required data changed
          if (
            currentNavItemId != null &&
            href != null &&
            previousItem != null &&
            (hrefChanged || labelsChanged) &&
            !currentMutation.isPending
          ) {
            // update the dynamic navigation item
            currentMutation.mutate({
              data: {
                id: currentNavItemId,
                href,
                icon: previousItem.icon,
                navItemLabels: effectiveNavItemLabels,
              },
            });
          }
        }
      }

      const nextStr = nextUrlSearchParams.toString();
      const currStr = currentSearchParams.toString();
      const isDiff =
        nextStr.length !== currStr.length ||
        [...nextUrlSearchParams.entries()].some(
          ([key, value]) => !currentSearchParams.getAll(key).includes(value)
        );
      if (isDiff) {
        setSearchParamsRef.current([...nextUrlSearchParams.entries()], {
          replace: true,
        });
      }
    }
  }, [
    filtersToSet,
    allFilterUrlKeys,
    searchString,
    cachedPageFilterDefinitions,
  ]);
}

type ResolveEffectiveNavItemLabelsArgs = {
  pathnameChanged: boolean;
  storedNavItemLabels: NavItemLabelDto[] | null | undefined;
  resolvedFromUrl: NavItemLabelDto[];
  isComparisonAndFilterDataAvailable?: boolean;
};

/**
 * Chooses which nav item labels to write when syncing detail-page filter state.
 */
function resolveEffectiveNavItemLabels({
  pathnameChanged,
  storedNavItemLabels,
  resolvedFromUrl,
  isComparisonAndFilterDataAvailable,
}: ResolveEffectiveNavItemLabelsArgs): NavItemLabelDto[] {
  // Comparison pages: refresh discriminator from filter option labels while
  // preserving other stored segments (e.g. pageTitle), including on sub-pages.
  if (isComparisonAndFilterDataAvailable) {
    return mergeComparisonNavItemLabels(storedNavItemLabels, resolvedFromUrl);
  }

  if (pathnameChanged) {
    // Sub-tree navigation changes completeSlug; keep stored labels, only refresh href.
    return storedNavItemLabels ?? [];
  }

  if (storedNavItemLabels != null && storedNavItemLabels.length > 0) {
    // Same pathname: human-readable labels from table links are not in the URL.
    return storedNavItemLabels;
  }

  if (storedNavItemLabels === null) {
    // Stale customization row explicitly marked null; backfill from URL.
    return resolvedFromUrl;
  }

  // undefined or empty array: no stored labels to preserve.
  return [];
}

// Checks if two hrefs are equal.
// They are considered equal if the pathname matches and the search parameters are equal regardless of the order.
function equalHref(a: string, b: string) {
  const urlA = new URL(a, globalThis.location.origin);
  const urlB = new URL(b, globalThis.location.origin);

  return (
    urlA.pathname === urlB.pathname &&
    urlA.searchParams.size === urlB.searchParams.size &&
    [...urlA.searchParams.entries()].every(([key, value]) =>
      urlB.searchParams.getAll(key).includes(value)
    )
  );
}

function getQueryOptions() {
  return queryOptions({
    queryKey: prependRoleId(['navigationCustomization']),
    queryFn: fetchNavigationCustomization,
  });
}

function useUpdateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateNavigationCustomization,
    onMutate: async (newNavItem) => {
      await queryClient.cancelQueries(getQueryOptions());
      const previousNavigationCustomization = queryClient.getQueryData(
        getQueryOptions().queryKey
      );

      // optimistically update to the new value
      queryClient.setQueryData(getQueryOptions().queryKey, (old) =>
        old
          ? {
              ...old,
              data: {
                dynamicNavigationItems: old.data.dynamicNavigationItems.map(
                  (navItem) =>
                    navItem.id === newNavItem.data.id
                      ? { ...navItem, ...newNavItem.data }
                      : navItem
                ),
              },
            }
          : undefined
      );

      return { previousNavigationCustomization };
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(
        getQueryOptions().queryKey,
        context?.previousNavigationCustomization
      );
    },
  });
}
